import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { StatusIndicator } from '@openx/components-core/lib/StatusIndicator/StatusIndicator.tsx';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { type DealApi, type DealOaApi, dealStatusForm, type PmpDealType, pmpDealType } from '@openx/types/deal.ts';
import { UserRole } from '@openx/types/user.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DealDetailsSectionProps } from '../../../../types.ts';
import { getDealsLabels } from '../../../DealForm/utils/dealFormUtils.ts';
import { useDealContext } from '../../../utils/context/DealContext.tsx';
import type { IUsePmpDealTypeFetch, UseExpectedAdCategoryFetch } from '../../../utils/context/DealHookTypes.ts';
import { getDealStatusLabel } from '../../../utils/getDealStatusLabel.ts';
import { useGetDealSourceName } from '../../hooks/useGetDealSourceName.ts';
import { ParticipantsDemandPartners, type ParticipantsDemandPartnersDeal } from '../ParticipantsDemandPartners.tsx';
import { AgencyRender } from './components/AgencyRender.tsx';
import { BrandRender } from './components/BrandRender.tsx';
import { OXSBuyerRender } from './components/OXSBuyerRender.tsx';

export type DetailsSectionDeal = Pick<
  DealApi,
  | 'status'
  | 'agency_salesforce_id'
  | 'brand_salesforce_id'
  | 'pmp_deal_type'
  | 'deal_source'
  | 'lifetime_impression_goal'
  | 'account_executive'
  | 'account_manager'
  | 'expected_ad_category'
  | 'name'
  | 'error'
  | 'buyer_account_id'
> &
  Partial<Pick<DealOaApi, 'publisher_name' | 'is_direct_deal'>> &
  ParticipantsDemandPartnersDeal;

export function DealDetailsSection<T extends DetailsSectionDeal>({ deal, historyMode }: DealDetailsSectionProps<T>) {
  const { useIsAllowed, isOa, isPub } = usePermissionContext();
  const { isAllowed, accessUser } = useIsAllowed();

  const isOaInternal = useMemo(() => !!accessUser?.roles.includes(UserRole.NETWORK_OA_INTERNAL), [accessUser?.roles]);

  const { t } = useTranslation();

  const {
    useExpectedAdCategoryFetch,
    useParticipantFunctions,
    usePmpDealTypeFetch,
    useAgenciesOptionsFetch,
    useBrandsOptionsFetch,
  } = useDealContext();
  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();

  const {
    agency_salesforce_id,
    brand_salesforce_id,
    pmp_deal_type,
    deal_source,
    lifetime_impression_goal,
    account_executive,
    account_manager,
    expected_ad_category,
    status,
    error,
    buyer_account_id,
    publisher_name,
    is_direct_deal,
  } = deal;

  const labels = getDealsLabels(t);
  const { dealSourceValue } = useGetDealSourceName({ dealSourceId: deal_source });
  const shouldDisplayErrorAlert = status === 'Error' && !!error;

  const isAllowedInternal = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));
  const shouldDisplayOXSBuyer = isAllowedInternal && isPub;
  const shouldDisplayPublisher = isOa && !!is_direct_deal;

  return (
    <FormSection
      divider
      historyMode={historyMode}
      title={t('Deal Details')}
      description={<StatusIndicator status={dealStatusForm[deal.status]} label={getDealStatusLabel(deal.status)} />}
    >
      {shouldDisplayErrorAlert && (
        <Alert severity="error">
          {!!error.timestamp && (
            <Typography component="span" variant="body2Bold">
              {error.timestamp}{' '}
            </Typography>
          )}
          <Typography component="span" variant="body2">
            {error.message}
          </Typography>
        </Alert>
      )}
      {historyMode && <FieldDetails title={labels.name} value={deal.name} />}

      {!isOa && usePmpDealTypeFetch && (
        <PmpDealTypeRender usePmpDealTypeFetch={usePmpDealTypeFetch} pmp_deal_type={pmp_deal_type} />
      )}

      {((isBefInstance && pmp_deal_type === pmpDealType.WithinOpenAuction) || isOa) && useExpectedAdCategoryFetch && (
        <ExpectedAdCategoryRender
          adCategory={expected_ad_category}
          useExpectedAdCategoryFetch={useExpectedAdCategoryFetch}
        />
      )}

      {!isOa && pmp_deal_type === pmpDealType.Guaranteed && (
        <FieldDetails title={labels.lifetime_impression_goal} value={lifetime_impression_goal} />
      )}

      <FieldDetails title={labels.deal_source} value={dealSourceValue} />

      {shouldDisplayPublisher && <FieldDetails title={labels.publisher_name} value={publisher_name} />}

      {(isAllowedInternal || isOaInternal) && (
        <>
          <AgencyRender useAgencyFetch={useAgenciesOptionsFetch} agencySalesforceId={agency_salesforce_id} />
          <BrandRender useBrandFetch={useBrandsOptionsFetch} brandSalesforceId={brand_salesforce_id} />
        </>
      )}

      {shouldDisplayOXSBuyer && <OXSBuyerRender pmpDealTypeValue={pmp_deal_type} buyerAccountId={buyer_account_id} />}

      {!isOa && <FieldDetails title={labels.account_executive} value={account_executive} />}
      {!isOa && <FieldDetails title={labels.account_manager} value={account_manager} />}

      {isOa && useParticipantFunctions && (
        <ParticipantsDemandPartners<T> deal={deal} useParticipantFunctions={useParticipantFunctions} />
      )}
    </FormSection>
  );
}

const PmpDealTypeRender = ({
  usePmpDealTypeFetch,
  pmp_deal_type,
}: {
  usePmpDealTypeFetch: IUsePmpDealTypeFetch;
  pmp_deal_type: PmpDealType;
}) => {
  const { t } = useTranslation();
  const labels = getDealsLabels(t);

  const { pmpDealTypes, pmpDealTypesLoading } = usePmpDealTypeFetch();

  return (
    <FieldDetails
      loading={pmpDealTypesLoading}
      title={labels.pmp_deal_type}
      value={pmpDealTypes[pmp_deal_type]?.name}
    />
  );
};

type ExpectedAdCategoryRenderProps = {
  useExpectedAdCategoryFetch: UseExpectedAdCategoryFetch;
  adCategory: DealApi['expected_ad_category'];
};

const ExpectedAdCategoryRender = ({ adCategory, useExpectedAdCategoryFetch }: ExpectedAdCategoryRenderProps) => {
  const { t } = useTranslation();
  const labels = getDealsLabels(t);

  const { expectedAdCategoryMap, isExpectedAdCategoryLoading } = useExpectedAdCategoryFetch(true);

  return (
    <Box
      sx={{
        mb: 1.5,
        mt: -0.5,
      }}
    >
      <IndentedSections>
        <FieldDetails
          loading={isExpectedAdCategoryLoading}
          value={(adCategory && expectedAdCategoryMap[adCategory]?.name) || adCategory}
          title={labels.expected_ad_category}
        />
      </IndentedSections>
    </Box>
  );
};
